/**
 * Send Vendor Sign-up Link dialog.
 *
 * Landlord enters the vendor's email + (optional) personal note. Backend
 * generates a 7-day single-use link, emails it to the vendor, and returns
 * the URL so the landlord can also copy/paste if they prefer to send it
 * via SMS or another channel.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Send, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function SendVendorSignupDialog({ open, onOpenChange, onIssued }) {
  const [form, setForm] = useState({ email: "", company_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const reset = () => {
    setForm({ email: "", company_name: "", note: "" });
    setIssued(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/signup-links", form);
      setIssued(data);
      if (onIssued) onIssued();
      if (data.email_sent) {
        toast.success("Sign-up email sent to the vendor");
      } else if (data.email_status === "mocked") {
        toast.warning(
          "Email is not configured for your workspace — please copy the URL below and send it to the vendor manually.",
          { duration: 8000 },
        );
      } else {
        toast.warning("Link created — email failed, please copy/paste the URL below");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create link");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!issued?.url) return;
    try {
      await navigator.clipboard.writeText(issued.url);
      toast.success("Sign-up URL copied");
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-5" data-testid="send-vendor-signup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" /> Send vendor sign-up form
          </DialogTitle>
        </DialogHeader>

        {issued ? (
          <div className="space-y-3" data-testid="vendor-signup-issued">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <div className="text-sm font-semibold">Sign-up link created</div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {issued.email_sent
                ? <>An email was sent to <b>{form.email}</b> with the form link. The link is valid for 7 days and can only be used once.</>
                : issued.email_status === "mocked"
                  ? <>
                      Email is not configured for your workspace yet. The link below is valid for 7 days — please copy it and send it to <b>{form.email}</b> manually (text, WhatsApp, your own email client, etc.). To enable automatic delivery, connect a BYO SMTP account or contact support to provision Resend.
                    </>
                  : <>The link was created but the email could not be sent. Please copy the URL below and send it to <b>{form.email}</b> manually.</>}
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-2.5 break-all font-mono text-[11px] text-slate-700">
              {issued.url}
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={copyUrl}
                className="btn-outline-dark text-sm flex items-center gap-1.5"
                data-testid="copy-vendor-signup-url"
              >
                <Copy className="w-4 h-4" /> Copy URL
              </button>
              <button
                type="button"
                onClick={() => handleClose(false)}
                className="btn-primary-dark text-sm"
                data-testid="vendor-signup-close-btn"
              >
                Done
              </button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              Send a vendor a unique link to fill out their own profile (legal name, mailing address, EIN/SSN, COI, etc.).
              Saves you typing and ensures their W-9 details come straight from them.
            </p>

            <Field label="Vendor email" required>
              <input
                required
                type="email"
                className="input-clean w-full"
                data-testid="vsi-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="vendor@example.com"
              />
            </Field>

            <Field label="Company name (optional)" hint="Pre-fills the form so the vendor doesn't need to retype it">
              <input
                className="input-clean w-full"
                data-testid="vsi-company-input"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="ACME Plumbing LLC"
              />
            </Field>

            <Field label="Personal note (optional)" hint="Shown at the top of the sign-up page and in the email">
              <textarea
                rows={2}
                className="input-clean w-full"
                data-testid="vsi-note-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Welcome aboard! Please complete this so we can issue you a 1099 next January."
              />
            </Field>

            <DialogFooter>
              <button type="button" onClick={() => handleClose(false)} className="btn-outline-dark text-sm" data-testid="vsi-cancel-btn">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="btn-primary-dark text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid="vsi-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "Sending…" : "Send sign-up form"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-500 mb-1">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}
